import { useContext, useState } from 'react'
// components
import { Box, Popper, Typography } from '@mui/material'
import DashboardContainer from 'components/smart/dashboard-container/DashboardContainer'
import TimeAttendancePanel from '../components/TimeAttendancePanel'
import ShiftDetails from '../components/ShiftDetails'
import AdxDateRangePicker from 'components/custom/AdxDateRangePicker'
import AdxButton from 'components/custom/AdxButton'
import DashboardDetailsDrawer from '../components/DashboardDetailsDrawer'
import AttendanceLabelRenderer, { renderDay } from 'components/custom/AdxLabelRenderer'
import { ReactComponent as CalendarIcon } from 'assets/icons/svg/Calendar.svg'

// services
import { useQuery } from 'react-query'
import { authContext } from 'contexts/AuthContext'
import {
  calculateTimeDifferenceFormatted,
  formatTimeToAmPm,
  timeObjectToDateConverter,
} from 'services/helper/DateTime'
import { addDays, format, startOfWeek } from 'date-fns'
import { getAttendanceDashboard } from 'services/api/time-attendance'

// icons
import AdxTableV2 from 'components/custom/AdxTableV2'

const TimeAttendance = () => {
  const { auth } = useContext(authContext)
  const staffId = auth?.data?.staff?.id
  const currentDateObject = startOfWeek(new Date(), { weekStartsOn: 1 })
  const currentYear = currentDateObject.getFullYear()
  const oneWeekAfter = addDays(currentDateObject, 6)

  const [selectedDate, setSelectedDate] = useState({
    start: currentDateObject,
    end: oneWeekAfter,
  })

  const [anchorEl, setAnchorEl] = useState(null)

  const handleClick = (event) => {
    setAnchorEl(anchorEl ? null : event.currentTarget)
  }

  const open = Boolean(anchorEl)
  const id = open ? 'simple-popper' : undefined

  const [filterValues, setFilterValues] = useState({
    startDate: currentDateObject,
    endDate: oneWeekAfter,
  })

  const resetFilter = () => {
    setAnchorEl(null)
    setFilterValues({
      startDate: currentDateObject,
      endDate: oneWeekAfter,
    })
    setSelectedDate({
      startDate: currentDateObject,
      endDate: oneWeekAfter,
    })
  }

  const handleApplyFilter = () => {
    setFilterValues({
      startDate: selectedDate.start,
      endDate: selectedDate.end,
    })
    setAnchorEl(null)
  }

  const convertedStartDate = format(filterValues?.startDate, 'yyyy-MM-dd')
  const convertedEndDate = format(filterValues?.endDate, 'yyyy-MM-dd')

  // should probably refactor dashboardData to prevent prop drilling, use context maybe? now data is passed to -> DashboardDetailsDrawer, TimeAttendancePanel -> ClockInDrawer
  const { data: dashboardData, isLoading: dashboardIsLoading } = useQuery(
    ['getAttendanceDashboard', staffId, convertedStartDate, convertedEndDate],
    async () => {
      const { data } = await getAttendanceDashboard(convertedStartDate, convertedEndDate)
      return data
    },
    {
      enabled: !!staffId,
      keepPreviousData: false,
    }
  )

  const tableColumn = [
    {
      id: 1,
      Header: 'Date',
      accessor: 'date',
      Cell: (props) => {
        const { value } = props
        return (
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              alignItems: 'center',
              p: 2,
              bgcolor: { xs: 'white.main', md: '' },
            }}
          >
            <Typography>{renderDay(value)}</Typography>
          </Box>
        )
      },
    },
    {
      id: 2,
      Header: 'Clock in',
      accessor: 'attendance',
      Cell: (props) => {
        const { value } = props
        return (
          <Box sx={{ p: 2 }}>
            <Typography>
              {value?.clockin?.clockin ? formatTimeToAmPm(value?.clockin?.clockin) : '--:--'}
            </Typography>
          </Box>
        )
      },
    },
    {
      id: 3,
      Header: 'Clock out',
      accessor: 'attendance',
      Cell: (props) => {
        const { value } = props
        return (
          <Box sx={{ p: 2 }}>
            <Typography>
              {value?.clockout?.clockout ? formatTimeToAmPm(value?.clockout?.clockout) : '--:--'}
            </Typography>
          </Box>
        )
      },
    },
    {
      id: 4,
      Header: 'Duration',
      accessor: 'attendance',
      Cell: (props) => {
        const { value } = props

        // value is the "attendance" object
        const totalHours = value?.total_hours // e.g. "00:05:10" from BE

        return (
          <Box sx={{ p: 2 }}>
            <Typography>{totalHours ? `${totalHours} hrs` : '--:--'}</Typography>
          </Box>
        )
      },
    },
    {
      id: 5,
      Header: 'Overtime',
      accessor: 'attendance.overtime',
      Cell: (props) => {
        const { value } = props

        return (
          <Box sx={{ p: 2 }}>
            <Typography sx={{}}>{value ? `+ ${value} hrs` : '--:--'}</Typography>
          </Box>
        )
      },
    },
    {
      id: 6,
      Header: 'Status',
      accessor: 'attendance.status',
      Cell: (props) => {
        const { value } = props
        return (
          <Box sx={{ px: 2, width: 'fit-content', maxWidth: '200px' }}>
            <AttendanceLabelRenderer
              status={value}
              withText
              withChip
              isHoliday={props?.row?.original?.is_holiday}
            />
          </Box>
        )
      },
    },
  ]

  const [drawerData, setDrawerData] = useState(null)
  const [drawerState, setDrawerState] = useState({ right: false })

  const handleRowClick = (item) => {
    setDrawerData(item)
    setDrawerState({ right: true })
  }

  return (
    <DashboardContainer headerTitle="Time & Attendance" isBeta>
      <Box display={'flex'} flexDirection={'column'} gap={2}>
        <TimeAttendancePanel data={dashboardData} />
        {/* <TimeAttendancePanel data={{ ...dashboardData, shift: null }} /> */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography sx={{ color: 'light-gray-text.main' }}>Daily attendance record</Typography>
          <ShiftDetails shiftId={dashboardData?.shift?.id} />
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: 'white.main',
            border: 1,
            borderColor: 'gray-line.main',
            borderRadius: '4.5px',
            overflow: 'clip',
          }}
        >
          <Box
            sx={{
              padding: '16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: 1,
              borderColor: 'gray-line.main',
            }}
          >
            <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <AdxButton
                variant={'outlined'}
                onClick={handleClick}
                sx={{ borderColor: 'gray-line.main', width: { xs: '90%', md: 'auto' } }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    width: '100%',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Typography sx={{ color: 'light-gray-text.main', fontWeight: 400 }}>
                      Time period:
                    </Typography>
                    <Typography sx={{ color: 'dark-text.main', fontWeight: 500 }}>
                      {timeObjectToDateConverter(filterValues?.startDate)}
                      {filterValues?.endDate &&
                        ` - ${timeObjectToDateConverter(filterValues.endDate)}`}
                    </Typography>
                  </Box>
                  <CalendarIcon width={25} color="#2666BE" />
                </Box>
              </AdxButton>
              <Popper
                id={id}
                open={open}
                anchorEl={anchorEl}
                placement={'bottom-start'}
                sx={{ zIndex: 500, minWidth: '300px', maxWidth: '700px' }}
              >
                <Box
                  sx={{
                    border: 1,
                    borderColor: 'gray-line.main',
                    borderRadius: '4.5px',
                    p: 1,
                    bgcolor: 'background.paper',
                  }}
                >
                  <AdxDateRangePicker
                    selectedYear={currentYear}
                    selectedDate={selectedDate}
                    setSelectedDate={setSelectedDate}
                  />
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'end',
                      gap: '8px',
                      alignItems: 'center',
                      padding: 2,
                      borderTop: 1,
                      marginX: -1,
                      borderTopColor: 'gray-line.main',
                      bgcolor: 'primary.light',
                      mt: 2,
                    }}
                  >
                    <AdxButton variant="outlined" sx={{ width: '150px' }} onClick={resetFilter}>
                      Cancel
                    </AdxButton>
                    <AdxButton
                      sx={{ width: '150px' }}
                      onClick={handleApplyFilter}
                      disabled={selectedDate.end === null}
                    >
                      Apply
                    </AdxButton>
                  </Box>
                </Box>
              </Popper>
              <AdxButton
                variant="outlined"
                sx={{
                  borderColor:
                    filterValues.startDate.getTime() === currentDateObject.getTime() &&
                      filterValues.endDate.getTime() === oneWeekAfter.getTime()
                      ? 'primary.main'
                      : 'gray-line.main',
                  color:
                    filterValues.startDate.getTime() === currentDateObject.getTime() &&
                      filterValues.endDate.getTime() === oneWeekAfter.getTime()
                      ? 'primary.contrastText'
                      : 'gray-line.main',
                  '&:hover': {
                    color: 'primary.main',
                    borderColor: 'primary.main',
                  },
                }}
                onClick={() =>
                  setFilterValues({
                    startDate: currentDateObject,
                    endDate: oneWeekAfter,
                  })
                }
              >
                This week
              </AdxButton>
            </Box>
          </Box>
          <AdxTableV2
            onClick={handleRowClick}
            rowClickable
            column={tableColumn}
            data={dashboardData?.attendances || []}
            allColumnSorted={false}
            highlightWhenHovered
            stickyHeader={true}
            stickyFirstColumn={true}
            height={{ xs: 'calc(100vh - 200px)', lg: 'calc(100vh - 447px)' }}
          />
        </Box>
        <DashboardDetailsDrawer
          data={drawerData}
          drawerState={drawerState}
          setDrawerState={setDrawerState}
        />
      </Box>
    </DashboardContainer>
  )
}

export default TimeAttendance
